import { useRef, useState } from 'react';
import { Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from './ui/input-otp';
import { apiFetch, getSessionToken, setSessionToken } from '../lib/apiClient';
import { supabase } from '../lib/supabaseClient';
import { COUNTRY_OPTIONS, isValidE164, maskPhone, toE164 } from '../lib/phoneUtils';

/** Restore Google OAuth session if verifyOtp swapped the Supabase session. */
async function restoreGoogleSessionIfNeeded(savedSession) {
  if (!savedSession?.access_token || !savedSession?.refresh_token || !supabase) {
    return false;
  }
  const { data: current } = await supabase.auth.getSession();
  if (current?.session?.access_token === savedSession.access_token) {
    return false;
  }
  const { error } = await supabase.auth.setSession({
    access_token: savedSession.access_token,
    refresh_token: savedSession.refresh_token,
  });
  if (error) {
    console.error('[PhoneVerification] setSession restore failed:', error);
    throw error;
  }
  setSessionToken(savedSession.access_token);
  return true;
}

export default function PhoneVerificationModal({ open, onVerified }) {
  const [step, setStep] = useState('phone');
  const [countryCode, setCountryCode] = useState('US');
  const [nationalNumber, setNationalNumber] = useState('');
  const [e164Phone, setE164Phone] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Supabase session (Google). Null for Apple/demo Mongo sessions.
  const supabaseSessionRef = useRef(null);
  // API bearer captured before OTP so verifyOtp cannot overwrite Apple/demo tokens.
  const apiAuthTokenRef = useRef('');

  const selectedCountry = COUNTRY_OPTIONS.find((c) => c.code === countryCode) || COUNTRY_OPTIONS[0];

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const phone = toE164(selectedCountry.dial, nationalNumber);
    if (!isValidE164(phone)) {
      toast.error('Enter a valid phone number');
      return;
    }
    if (!supabase) {
      toast.error('Phone verification is not configured');
      return;
    }

    setSending(true);
    try {
      apiAuthTokenRef.current = getSessionToken() || '';
      const { data: sessionData } = await supabase.auth.getSession();
      supabaseSessionRef.current = sessionData?.session ?? null;

      if (!apiAuthTokenRef.current && !supabaseSessionRef.current?.access_token) {
        toast.error('Session expired — please sign in again and retry phone verification.');
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { channel: 'sms' },
      });
      if (error) {
        toast.error(error.message || 'Failed to send verification code');
        return;
      }

      setE164Phone(phone);
      setStep('otp');
      toast.success('Verification code sent');
    } catch (err) {
      console.error('signInWithOtp failed:', err);
      toast.error('Failed to send verification code');
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    if (!supabase || !e164Phone) {
      toast.error('Phone verification is not configured');
      return;
    }

    const savedSupabaseSession = supabaseSessionRef.current;
    const savedApiToken = apiAuthTokenRef.current;

    setVerifying(true);
    try {
      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        phone: e164Phone,
        token: otp,
        type: 'sms',
      });
      if (verifyError) {
        console.error('[PhoneVerification] verifyOtp failed:', verifyError);
        toast.error(`Code verification failed: ${verifyError.message || 'Invalid code'}`);
        return;
      }

      // Capture the phone-session token NOW (before restoring the login session).
      // The backend verifies this token to prove control of the phone number.
      const phoneProofToken = verifyData?.session?.access_token || null;
      if (!phoneProofToken) {
        console.error('[PhoneVerification] missing phone proof token after verifyOtp');
        toast.error('Phone verification failed — please try again.');
        return;
      }

      if (savedSupabaseSession?.access_token) {
        try {
          await restoreGoogleSessionIfNeeded(savedSupabaseSession);
        } catch (restoreError) {
          console.error('[PhoneVerification] Google session restore failed:', restoreError);
          toast.error('Could not restore your sign-in session. Please sign in again.');
          return;
        }
        setSessionToken(savedSupabaseSession.access_token);
      } else if (savedApiToken) {
        // Apple/demo: re-apply Mongo session token overwritten by verifyOtp SIGNED_IN.
        setSessionToken(savedApiToken);
      } else {
        console.error('[PhoneVerification] missing auth token before PATCH');
        toast.error('Session expired — please sign in again and retry phone verification.');
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 0));

      const response = await apiFetch('/api/auth/phone', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: e164Phone,
          phone_verified: true,
          phone_proof_token: phoneProofToken,
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        console.error('[PhoneVerification] PATCH /api/auth/phone failed:', {
          status: response.status,
          statusText: response.statusText,
          body: errBody,
        });
        const detail = typeof errBody.detail === 'string' ? errBody.detail : null;
        toast.error(
          detail
            ? `Could not save phone (${response.status}): ${detail}`
            : `Could not save phone — server returned ${response.status}`,
        );
        return;
      }

      const updatedUser = await response.json();
      toast.success('Phone verified');
      onVerified(updatedUser);
    } catch (err) {
      console.error('[PhoneVerification] unexpected error in handleVerifyOtp:', err);
      toast.error('Phone verification failed unexpectedly');
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="z-[100] sm:max-w-md [&>button.absolute]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Phone className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Verify your phone</DialogTitle>
          <DialogDescription className="text-center">
            {step === 'phone'
              ? 'Add your mobile number to place orders and receive pickup updates.'
              : `Enter the 6-digit code sent to ${maskPhone(e164Phone)}`}
          </DialogDescription>
        </DialogHeader>

        {step === 'phone' ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone-country">Country</Label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger id="phone-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.label} (+{c.dial})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone-number">Phone number</Label>
              <div className="flex gap-2">
                <div className="flex h-9 w-16 shrink-0 items-center justify-center rounded-md border border-input bg-muted text-sm text-muted-foreground">
                  +{selectedCountry.dial}
                </div>
                <Input
                  id="phone-number"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="555 123 4567"
                  value={nationalNumber}
                  onChange={(ev) => setNationalNumber(ev.target.value.replace(/[^\d\s-]/g, ''))}
                  required
                />
              </div>
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending code…
                </>
              ) : (
                'Send verification code'
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={verifying || otp.length !== 6}>
              {verifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                'Verify & continue'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleBack}
              disabled={verifying}
            >
              Change phone number
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
